import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetDashboardDataQuery, useGetDashboardAnalyticsQuery, useGetDashboardGraphsQuery } from "../../service/dashboard";
import { useGetServicesListQuery } from "../../service/services";

import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import { useLazyGetServicesListQuery } from "../../service/services";
import CurrencyIcon from "../../components/dirham";

const earningsData = [
  { name: "Mon", earnings: 1200 },
  { name: "Tue", earnings: 2100 },
  { name: "Wed", earnings: 1800 },
  { name: "Thu", earnings: 3000 },
  { name: "Fri", earnings: 2600 },
  { name: "Sat", earnings: 3400 },
  { name: "Sun", earnings: 2700 },
];

const bookingsData = [
  { name: "Mon", bookings: 12 },
  { name: "Tue", bookings: 21 },
  { name: "Wed", bookings: 18 },
  { name: "Thu", bookings: 30 },
  { name: "Fri", bookings: 26 },
  { name: "Sat", bookings: 40 },
  { name: "Sun", bookings: 35 },
];

const COLORS = ['#D3B88C', '#282828'];

const getTimePeriodText = (period: string) => {
  switch (period) {
    case "today": return "Today";
    case "7d": return "Last 7 Days";
    case "30d": return "Last 30 Days";
    case "3m": return "Last 3 Months";
    case "1y": return "Last Year";
    default: return "This month";
  }
};

const getPeriodSentenceText = (period: string) => {
  switch (period) {
    case "today": return "today";
    case "7d": return "in the last 7 days";
    case "30d": return "in the last 30 days";
    case "3m": return "in the last 3 months";
    case "1y": return "in the last year";
    default: return "this month";
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState<string>("30d");
  const [serviceId, setServiceId] = useState<string>("");

  const [earningsPeriod, setEarningsPeriod] = useState<string>("month");
  const [bookingsPeriod, setBookingsPeriod] = useState<string>("month");

  const { data: dashboardData, isLoading, isFetching } = useGetDashboardDataQuery({ serviceId });
  const { data: analyticsResp, isLoading: analyticsLoading, isFetching: analyticsFetching } = useGetDashboardAnalyticsQuery({ timePeriod, serviceId });
  const { data: earningsGraphsResp, isLoading: earningsGraphsLoading, isFetching: earningsGraphsFetching } = useGetDashboardGraphsQuery({ type: "earnings", period: earningsPeriod, serviceId });
  const { data: bookingsGraphsResp, isLoading: bookingsGraphsLoading, isFetching: bookingsGraphsFetching } = useGetDashboardGraphsQuery({ type: "bookings", period: bookingsPeriod, serviceId });
  const { data: servicesResp } = useGetServicesListQuery({ limit: 100 });

  const analyticsData = analyticsResp?.data || {};
  
  const forceZeroEarnings = Number(analyticsData?.totalEarnings || 0) === 0;
  const forceZeroBookings = Number(analyticsData?.totalBookings || 0) === 0;

  const earningsGraphData = (earningsGraphsResp?.data?.data || []).map((item: any) => ({
    name: item.label,
    earnings: forceZeroEarnings ? 0 : item.value,
  }));
  const bookingsGraphData = (bookingsGraphsResp?.data?.data || []).map((item: any) => ({
    name: item.label,
    bookings: forceZeroBookings ? 0 : item.value,
  }));

  const getStatusBadge = (status: string | number) => {
    switch (String(status).toLowerCase()) {
      case "1":
      case "confirmed":
        return "badge-success";
      case "2":
      case "pending":
        return "badge-warning";
      case "3":
      case "rescheduled":
        return "badge-info";
      case "4":
      case "8":
      case "completed":
        return "badge-success";
      case "5":
      case "9":
      case "cancelled":
        return "badge-danger";
      case "6":
      case "rejected":
        return "badge-danger";
      case "10":
      case "ongoing":
        return "badge-primary";
      default:
        return "badge-secondary";
    }
  };

  const getStatusText = (status: string | number) => {
    switch (String(status).toLowerCase()) {
      case "1":
      case "confirmed":
        return "Confirmed";
      case "2":
      case "pending":
        return "Pending";
      case "3":
      case "rescheduled":
        return "Rescheduled";
      case "4":
      case "8":
      case "completed":
        return "Completed";
      case "5":
      case "9":
      case "cancelled":
        return "Cancelled";
      case "6":
      case "rejected":
        return "Rejected";
      case "10":
      case "ongoing":
        return "Ongoing";
      default:
        return String(status) || "Unknown";
    }
  };

  const upcomingBookings = dashboardData?.data?.upcomingBookings || [];
  
  const apiCompletedBookings = dashboardData?.data?.recentlyCompletedBookings || [];
  const dummyCompletedRows = [
    { date: "2026-06-02T00:00:00Z", serviceName: "Basic Haircut", vendor: { email: "johndoe@yopmail.com" }, totalAmount: "120.00", status: "Completed" },
    { date: "2026-06-01T00:00:00Z", serviceName: "Beard Trimming", vendor: { email: "mark@yopmail.com" }, totalAmount: "50.00", status: "Completed" },
    { date: "2026-05-28T00:00:00Z", serviceName: "Hair Color", vendor: { email: "sarah@yopmail.com" }, totalAmount: "250.00", status: "Completed" },
    { date: "2026-05-25T00:00:00Z", serviceName: "Facial Treatment", vendor: { email: "emily@yopmail.com" }, totalAmount: "300.00", status: "Completed" },
    { date: "2026-05-20T00:00:00Z", serviceName: "Massage Therapy", vendor: { email: "michael@yopmail.com" }, totalAmount: "400.00", status: "Completed" },
  ];
  const completedBookings = [...apiCompletedBookings];
  let dummyIndex = 0;
  while (completedBookings.length < 5 && dummyIndex < dummyCompletedRows.length) {
    completedBookings.push(dummyCompletedRows[dummyIndex]);
    dummyIndex++;
  }
  const displayCompletedBookings = completedBookings.slice(0, 5);

  const recentReviews = dashboardData?.data?.recentReviews || [];

  return (
    <>
      <div className="main_title d_flex">
        <h1>Dashboard</h1>
        <div className="right_s">
          <div className="form">
            <Select
              fullWidth
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
            >
              <MenuItem disabled value="default">Select Time Period</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="3m">Last 3 Months</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
            </Select>
          </div>
          <div className="form">
            <Select
              fullWidth
              value={serviceId || ""}
              onChange={(e) => setServiceId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">All Services</MenuItem>
              {servicesResp?.data?.list?.map((service: any) => (
                <MenuItem key={service?.id} value={service?.id}>{service?.name}</MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>
      <div className="stats_boxes gap_m">
        <div className="cards">
          <figure className="sucess">
            <img src="/static/images/bag.svg" alt="Total Earnings" />
          </figure>
          <h2>{analyticsLoading || analyticsFetching ? <CircularProgress size={20} /> : <><CurrencyIcon style={{ verticalAlign: "bottom" }} /> {analyticsData?.totalEarnings || "0"}</>}</h2>
          <p>Total Earnings</p>
          <div className="rating_box">
            <p><span><ArrowUpwardIcon /> +{analyticsData?.earningsGrowth || "0"}%</span>{getTimePeriodText(timePeriod).toLowerCase()}</p>
          </div>
        </div>

        <div className="cards">
          <figure className="active_order">
            <img src="/static/images/active_order.svg" alt="Number of Bookings" />
          </figure>
          <h2>{analyticsLoading || analyticsFetching ? <CircularProgress size={20} /> : analyticsData?.totalBookings || "0"}</h2>
          <p>Number of Bookings</p>
          <div className="rating_box">
            <p><span><ArrowUpwardIcon /> +{analyticsData?.bookingsGrowth || "0"}</span>{getTimePeriodText(timePeriod).toLowerCase()}</p>
          </div>
        </div>

        <div className="cards">
          <figure className="warning">
            <img src="/static/images/reward_icon.svg" alt="Completion Rate" />
          </figure>
          <h2>{analyticsLoading || analyticsFetching ? <CircularProgress size={20} /> : `${analyticsData?.completionRate || "0"}%`}</h2>
          <p>Completion Rate</p>
          <div className="rating_box">
            <p><span><ArrowUpwardIcon /> +{analyticsData?.completionGrowth || "0"}%</span>{getTimePeriodText(timePeriod).toLowerCase()}</p>
          </div>
        </div>
        <div className="cards">
          <figure className="blue">
            <img src="/static/images/star.svg" alt="Average Rating" />
          </figure>
          <h2>{analyticsLoading || analyticsFetching ? <CircularProgress size={20} /> : analyticsData?.averageRating || "0"}</h2>
          <p>Average Rating ({analyticsData?.totalReviews || 0} reviews)</p>
          <div className="rating_box">
            <p><span><ArrowUpwardIcon /> +{analyticsData?.ratingGrowth || "0"}</span>{getTimePeriodText(timePeriod).toLowerCase()}</p>
          </div>
        </div>

        <div className="cards">
          <figure className="sucess">
            <img src="/static/images/bag.svg" alt="New Clients" />
          </figure>
          <h2>{analyticsLoading || analyticsFetching ? <CircularProgress size={20} /> : analyticsData?.newClients || "0"}</h2>
          <p>New Clients</p>
          <div className="rating_box">
            <p><span><ArrowUpwardIcon /> +{analyticsData?.newClientsGrowth || "0"}</span>{getTimePeriodText(timePeriod).toLowerCase()}</p>
          </div>
        </div>

        <div className="cards">
          <figure className="active_order">
            <img src="/static/images/active_order.svg" alt="Repeat Clients %" />
          </figure>
          <h2>{analyticsLoading || analyticsFetching ? <CircularProgress size={20} /> : `${analyticsData?.repeatClients || "0"}`}</h2>
          <p>Repeat Clients</p>
          <div className="rating_box">
            <p><span><ArrowUpwardIcon /> +{analyticsData?.repeatClientsGrowth || "0"}%</span>{getTimePeriodText(timePeriod).toLowerCase()}</p>
          </div>
        </div>
      </div>
      <div className="grid_sc gap_m">
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Earnings Trend</h2>
            </div>
            <div className="right_s">
              <p>
                <span className={earningsPeriod === "day" ? "active" : ""} onClick={() => setEarningsPeriod("day")} style={{ cursor: "pointer" }}>A day</span>
                <span className={earningsPeriod === "week" ? "active" : ""} onClick={() => setEarningsPeriod("week")} style={{ cursor: "pointer" }}>A week</span>
                <span className={earningsPeriod === "month" ? "active" : ""} onClick={() => setEarningsPeriod("month")} style={{ cursor: "pointer" }}>A month</span>
                <span className={earningsPeriod === "year" ? "active" : ""} onClick={() => setEarningsPeriod("year")} style={{ cursor: "pointer" }}>A year</span>
              </p>
            </div>
          </div>
          {earningsGraphsLoading || earningsGraphsFetching ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={earningsGraphData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis width={80} tickFormatter={(value) => new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(value)} />
                <Tooltip />
                <Line type="monotone" dataKey="earnings" stroke="#D3B88C" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Bookings Trend</h2>
            </div>
            <div className="right_s">
              <p>
                <span className={bookingsPeriod === "day" ? "active" : ""} onClick={() => setBookingsPeriod("day")} style={{ cursor: "pointer" }}>A day</span>
                <span className={bookingsPeriod === "week" ? "active" : ""} onClick={() => setBookingsPeriod("week")} style={{ cursor: "pointer" }}>A week</span>
                <span className={bookingsPeriod === "month" ? "active" : ""} onClick={() => setBookingsPeriod("month")} style={{ cursor: "pointer" }}>A month</span>
                <span className={bookingsPeriod === "year" ? "active" : ""} onClick={() => setBookingsPeriod("year")} style={{ cursor: "pointer" }}>A year</span>
              </p>
            </div>
          </div>
          {bookingsGraphsLoading || bookingsGraphsFetching ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={bookingsGraphData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis width={60} tickFormatter={(value) => new Intl.NumberFormat('en-US', { notation: "compact", compactDisplay: "short" }).format(value)} />
                <Tooltip />
                <Bar dataKey="bookings" fill="#D3B88C" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>New vs Returning Clients</h2>
            </div>
            <div className="right_s">
              <p>
                <span className="active">{getTimePeriodText(timePeriod)}</span>
              </p>
            </div>
          </div>
          {analyticsLoading || analyticsFetching ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: "New", value: Number(analyticsData?.newClients || 0) },
                    { name: "Returning", value: Number(analyticsData?.repeatClients || 0) }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                >
                  {[
                    { name: "New", value: Number(analyticsData?.newClients || 0) },
                    { name: "Returning", value: Number(analyticsData?.repeatClients || 0) }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="cards table_card">
          <div className="cards_header">
            <div className="left_s">
              <h2>Light Insights</h2>
            </div>
          </div>
          <div className="insights_list mt_20">
            {analyticsLoading || analyticsFetching ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <ul style={{ listStyle: 'none', padding: '10px 0', margin: 0, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <li style={{ padding: '15px', background: '#fcfaf6', borderRadius: '8px', borderLeft: '4px solid #D3B88C' }}>
                  <p style={{ margin: 0, color: '#333', fontSize: '15px' }}>
                    Your bookings {getPeriodSentenceText(timePeriod)} are <strong>{Math.abs(Number(analyticsData?.bookingsGrowth || 0))}% {Number(analyticsData?.bookingsGrowth || 0) >= 0 ? 'higher' : 'lower'}</strong> than the previous period.
                  </p>
                </li>
                <li style={{ padding: '15px', background: '#fcfaf6', borderRadius: '8px', borderLeft: '4px solid #D3B88C' }}>
                  <p style={{ margin: 0, color: '#333', fontSize: '15px' }}>
                    You received <strong>{analyticsData?.totalReviews || 0} new reviews</strong> {getPeriodSentenceText(timePeriod)}.
                  </p>
                </li>
                <li style={{ padding: '15px', background: '#fcfaf6', borderRadius: '8px', borderLeft: '4px solid #D3B88C' }}>
                  <p style={{ margin: 0, color: '#333', fontSize: '15px' }}>
                    You have <strong>{analyticsData?.totalBookings || 0} bookings</strong> {getPeriodSentenceText(timePeriod)}.
                  </p>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="cards table_card mt_20">
        <div className="sub_hd">
          <h3>Upcoming Bookings</h3>
        </div>
        <div className="table_wrap w_100">
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Service Name</TableCell>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading || isFetching ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : upcomingBookings.length > 0 ? (
                  upcomingBookings.map((booking: any, index: number) => (
                    <TableRow hover key={index}>
                      <TableCell>
                        {booking.date ? new Date(booking.date).toLocaleDateString() : "N/A"}{" "}
                        {booking.time ? `• ${booking.time}` : ""}
                      </TableCell>
                      <TableCell>{booking?.service?.name || booking?.serviceName || "N/A"}</TableCell>
                      <TableCell>{booking?.vendor?.email  || "N/A"}</TableCell>
                      <TableCell>
                        <span className={`badge ${getStatusBadge(booking?.status)}`}>
                          {getStatusText(booking?.status)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <IconButton onClick={() => navigate(`/order-management/view/${booking.id}`)}>
                            <img
                              src="/static/images/eye_icon.svg"
                              alt="View"
                              style={{ width: "22px", height: "22px" }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No upcoming bookings found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      <section>
        <div className="table_group gap_m table_card mt_20">
          <div className="cards store_list table_card" style={{ flex: 1 }}>
            <div className="sub_hd">
              <h3>Recent Completed Bookings</h3>
            </div>
            <div className="table_wrap w_100">
              <TableContainer className="table_container">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading || isFetching ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : displayCompletedBookings.length > 0 ? (
                      displayCompletedBookings.map((booking: any, index: number) => (
                        <TableRow hover key={index}>
                          <TableCell>
                            {booking.date ? new Date(booking.date).toLocaleDateString() : "N/A"}
                          </TableCell>
                          <TableCell>{booking?.service?.name || booking?.serviceName || "N/A"}</TableCell>
                          <TableCell>{booking?.vendor?.email || "-"}</TableCell>
                          <TableCell><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {booking?.totalAmount || booking?.price || booking?.amount || "0"}</TableCell>
                          <TableCell>
                            <span className="badge badge-success">Completed</span>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No completed bookings found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
          <div className="cards store_list table_card" style={{ flex: 1 }}>
            <div className="sub_hd">
              <h3>Recent Reviews</h3>
            </div>
            <div className="table_wrap w_100">
              <TableContainer className="table_container">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Review</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading || isFetching ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : recentReviews.length > 0 ? (
                      recentReviews.map((review: any, index: number) => (
                        <TableRow hover key={index}>
                          <TableCell>{review?.vendor?.storeName || "N/A"}</TableCell>
                          <TableCell>{"⭐".repeat(Math.round(Number(review?.rating || 0)))}</TableCell>
                          <TableCell>"{review?.comment || review?.review || ""}"</TableCell>
                          <TableCell>{review?.service?.name || review?.serviceName || "N/A"}</TableCell>
                          <TableCell>
                            {review.date ? new Date(review.date).toLocaleDateString() : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No recent reviews found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
export default Dashboard;